import type { CoordinateExtent, Node } from '@xyflow/react';

import { getCountryName, getPrintableCorporationType } from '@commenda/utils';

import {
  LayoutGeometryHelper as G,
  TextMeasureHelper,
  type LayoutLine,
  type LayoutResult,
  type LineKind,
  type Rect,
  type TextMeasurer,
} from '../layout';
import type { ChartEntity, OwnershipModel, Relation } from '../model';

export type StructureEntityNodeData = {
  entity: ChartEntity;
  typeLabel: string | null;
  isUnlinked: boolean;
  isNameTruncated: boolean;
  nameLang: string | undefined;
};

export type StructureEntityFlowNode = Node<
  StructureEntityNodeData,
  'structureEntity'
>;

export interface LineLegendEntry {
  label: string;
  kinds: ReadonlyArray<LineKind>;
}

interface CanvasSize {
  width: number;
  height: number;
}

class StructureCanvasHelper {
  static readonly NODE_TYPE = 'structureEntity';

  static readonly NODE_SIZE: Readonly<CanvasSize> = {
    width: G.NODE_WIDTH,
    height: G.NODE_HEIGHT,
  };

  static readonly CARD_PADDING_LEFT = 12;

  static readonly CARD_PADDING_RIGHT = 16;

  static readonly CARD_GAP = 10;

  static readonly FLAG_SIZE = 24;

  // The 0.5px border takes a whole pixel on standard-density screens.
  static readonly CARD_BORDER = 1;

  static readonly NAME_FONT = `500 13px ${TextMeasureHelper.FONT_FAMILY}`;

  static readonly NAME_WIDTH =
    G.NODE_WIDTH -
    StructureCanvasHelper.CARD_PADDING_LEFT -
    StructureCanvasHelper.CARD_PADDING_RIGHT -
    StructureCanvasHelper.FLAG_SIZE -
    StructureCanvasHelper.CARD_GAP -
    2 * StructureCanvasHelper.CARD_BORDER;

  static readonly KANA = /[\u3040-\u30ff\u31f0-\u31ff]/;

  static readonly HANGUL = /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/;

  static readonly HAN = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/;

  // Chinese characters alone don't say which language they are written in, so the country does.
  static readonly HAN_LANG_BY_COUNTRY: Readonly<Record<string, string>> = {
    JP: 'ja',
    CN: 'zh-Hans',
    SG: 'zh-Hans',
    TW: 'zh-Hant',
    HK: 'zh-Hant',
    MO: 'zh-Hant',
    KR: 'ko',
  };

  // Lets the chart be dragged half a canvas past its edge, clear of the floating controls.
  static readonly PAN_OVERSCROLL_FRACTION = 0.5;

  static readonly MIN_PAN_PADDING = 100;

  // A zero-length dash with round caps draws a dot.
  static readonly LINE_DASH: Readonly<Record<LineKind, string | undefined>> = {
    equity: undefined,
    loop: undefined,
    limitedPartner: '0 4',
    generalPartner: '6 4',
    control: '6 4',
  };

  // One entry per line style, so kinds drawn alike share a row.
  static readonly LEGEND_ENTRIES: ReadonlyArray<LineLegendEntry> = [
    { label: 'Equity ownership', kinds: ['equity', 'loop'] },
    { label: 'Limited partner relationship', kinds: ['limitedPartner'] },
    {
      label: 'General partner or control',
      kinds: ['generalPartner', 'control'],
    },
  ];

  static readonly FOCUS_PAN_MS = 400;

  static readonly FOCUS_PULSE_MS = 1600;

  static readonly CHIP_CLASS = 'structure-chart-chip';

  static toFlowNodes(
    layout: Readonly<LayoutResult>,
    model: Readonly<OwnershipModel>,
    measurer: Readonly<TextMeasurer>
  ): StructureEntityFlowNode[] {
    const unlinkedIds = new Set(layout.tray?.entityIds ?? []);
    return layout.nodes.flatMap((node) => {
      const entity = model.entitiesById.get(node.entityId);
      if (!entity) return [];
      return [
        {
          id: String(node.entityId),
          type: StructureCanvasHelper.NODE_TYPE,
          position: { x: node.x, y: node.y },
          // The minimap sizes nodes from these; a read-only chart never gets the measured size back.
          initialWidth: node.width,
          initialHeight: node.height,
          selectable: !entity.isExternal,
          ariaLabel: StructureCanvasHelper.getAriaLabel(
            entity,
            unlinkedIds.has(node.entityId)
          ),
          data: {
            entity,
            typeLabel: StructureCanvasHelper.getTypeLabel(entity),
            isUnlinked: unlinkedIds.has(node.entityId),
            isNameTruncated:
              measurer.measure(entity.name, StructureCanvasHelper.NAME_FONT) >
              StructureCanvasHelper.NAME_WIDTH,
            nameLang: StructureCanvasHelper.getNameLang(
              entity.name,
              entity.source.incorporationCountry
            ),
          },
        },
      ];
    });
  }

  // A legend is only worth its space once the chart mixes line styles.
  static getLegendEntries(
    lines: ReadonlyArray<LayoutLine>
  ): ReadonlyArray<LineLegendEntry> {
    const kinds = new Set(lines.map((line) => line.kind));
    const entries = StructureCanvasHelper.LEGEND_ENTRIES.filter((entry) =>
      entry.kinds.some((kind) => kinds.has(kind))
    );
    return entries.length > 1 ? entries : [];
  }

  static getRelationIdsOf(
    entityId: number | undefined,
    relations: ReadonlyArray<Relation>
  ): ReadonlySet<string> {
    return new Set(
      relations
        .filter(
          (relation) =>
            relation.ownerId === entityId || relation.ownedId === entityId
        )
        .map((relation) => relation.id)
    );
  }

  static getTypeLabel(entity: Readonly<ChartEntity>): string | null {
    if (entity.legalFormName) return entity.legalFormName;
    const { businessEntityType, incorporationCountry } = entity.source;
    return businessEntityType && incorporationCountry
      ? getPrintableCorporationType(businessEntityType, incorporationCountry)
      : null;
  }

  // What a screen reader announces for a card, since the flag and the card style carry meaning too.
  static getAriaLabel(
    entity: Readonly<ChartEntity>,
    isUnlinked: boolean
  ): string {
    return [
      entity.name,
      StructureCanvasHelper.getTypeLabel(entity),
      getCountryName(entity.source.incorporationCountry),
      entity.isExternal ? 'outside the portfolio' : undefined,
      isUnlinked ? 'not linked to the structure' : undefined,
    ]
      .filter(Boolean)
      .join(', ');
  }

  static isRectInView(
    rect: Readonly<Rect>,
    transform: readonly [number, number, number],
    canvasSize: Readonly<CanvasSize>
  ): boolean {
    const [translateX, translateY, zoom] = transform;
    const left = rect.x * zoom + translateX;
    const top = rect.y * zoom + translateY;
    return (
      left >= 0 &&
      top >= 0 &&
      left + rect.width * zoom <= canvasSize.width &&
      top + rect.height * zoom <= canvasSize.height
    );
  }

  static getMotionMs(ms: number): number {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : ms;
  }

  // A CJK name carries its language so the browser draws it with that language's character forms
  // (a Japanese name in Japanese kanji, not the Chinese default many systems fall back to).
  static getNameLang(
    name: string,
    countryCode: string | null | undefined
  ): string | undefined {
    if (StructureCanvasHelper.KANA.test(name)) return 'ja';
    if (StructureCanvasHelper.HANGUL.test(name)) return 'ko';
    if (!StructureCanvasHelper.HAN.test(name) || !countryCode) return undefined;
    return StructureCanvasHelper.HAN_LANG_BY_COUNTRY[countryCode];
  }

  static getTranslateExtent(
    bounds: Readonly<Rect>,
    canvasSize: Readonly<CanvasSize>
  ): CoordinateExtent {
    const padX = Math.max(
      StructureCanvasHelper.MIN_PAN_PADDING,
      canvasSize.width * StructureCanvasHelper.PAN_OVERSCROLL_FRACTION
    );
    const padY = Math.max(
      StructureCanvasHelper.MIN_PAN_PADDING,
      canvasSize.height * StructureCanvasHelper.PAN_OVERSCROLL_FRACTION
    );
    return [
      [bounds.x - padX, bounds.y - padY],
      [bounds.x + bounds.width + padX, bounds.y + bounds.height + padY],
    ];
  }
}

export { StructureCanvasHelper };
